import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours in ms
const TOKEN_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes in ms

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(sessionStorage.getItem('token'));
  const inactivityTimer = useRef(null);
  const refreshTimer = useRef(null);
  const lastActivity = useRef(Date.now());

  const logout = useCallback(async () => {
    // Blacklist the token server-side before clearing locally
    const currentToken = sessionStorage.getItem('token');
    if (currentToken) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
      } catch {
        // Ignore — local cleanup still proceeds
      }
    }
    sessionStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    clearTimeout(inactivityTimer.current);
    clearInterval(refreshTimer.current);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    lastActivity.current = Date.now();
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      toast.info('Session expired due to inactivity. Please log in again.');
      logout();
    }, INACTIVITY_TIMEOUT);
  }, [logout]);

  const refreshToken = useCallback(async () => {
    if (!sessionStorage.getItem('token')) return;
    // Only refresh if user was active in the last refresh interval
    if (Date.now() - lastActivity.current > TOKEN_REFRESH_INTERVAL) return;
    try {
      const res = await axios.post(`${API}/auth/refresh`);
      const newToken = res.data.access_token;
      sessionStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
    } catch (e) {
      // Token invalid/expired — force logout
      logout();
    }
  }, [logout]);

  // Track user activity
  useEffect(() => {
    if (!user) return;

    const onActivity = () => resetInactivityTimer();
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    // Start timers
    resetInactivityTimer();
    refreshTimer.current = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);

    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      clearTimeout(inactivityTimer.current);
      clearInterval(refreshTimer.current);
    };
  }, [user, resetInactivityTimer, refreshToken]);

  // Global axios interceptor for 401 — auto logout on expired token
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          // Token expired or invalid — force logout
          toast.error('Your session has expired. Please log in again.');
          logout();
        }
        return Promise.reject(error);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token, logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    // Include device token header for trusted device check
    const deviceToken = localStorage.getItem('slotfleet_device_token');
    const headers = deviceToken ? { 'X-Device-Token': deviceToken } : {};
    const response = await axios.post(`${API}/auth/login`, { email, password }, { 
      headers,
      withCredentials: true 
    });
    
    // Check if MFA is required
    if (response.data.mfa_required) {
      return response.data; // Return MFA data without setting token
    }
    
    const { access_token, user: userData } = response.data;
    
    sessionStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const loginWithToken = async (accessToken) => {
    sessionStorage.setItem('token', accessToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setToken(accessToken);
    await fetchUser();
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    
    sessionStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === 'string') return user.role === roles;
    return roles.includes(user.role);
  };

  const canManageCompanies = () => hasRole(['super_admin', 'admin']);
  const canManageLocations = () => hasRole(['super_admin', 'admin', 'company_admin']);
  const canManageMachines = () => hasRole(['super_admin', 'admin', 'company_admin']);
  const canManagePartners = () => hasRole(['super_admin', 'admin', 'company_admin']);
  const canManageCollections = () => hasRole(['super_admin', 'admin', 'company_admin', 'location_manager']);
  const canViewOnly = () => hasRole(['location_user']);

  const isAuthenticated = !!user && !!token;

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated,
      login,
      loginWithToken,
      register,
      logout,
      hasRole,
      canManageCompanies,
      canManageLocations,
      canManageMachines,
      canManagePartners,
      canManageCollections,
      canViewOnly,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
